import { createSlice } from "@reduxjs/toolkit";
import { AUTH_TOKEN_KEY, MENU_ITEMS_KEY, PASSWORD_STATUS_KEY } from "../../constants/appConstants";
import jwtDecode from "jwt-decode";

// localStorage'dan passwordStatus'u oku
const getStoredPasswordStatus = () => {
  try {
    const stored = localStorage.getItem(PASSWORD_STATUS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const initialState = {
  user: {},
  isAuthenticated: false,
  role: {},
  showEmailVerificationDialog: false,
  showPasswordChangeDialog: false,
  showFirstLoginPasswordResetDialog: false,
  passwordStatus: getStoredPasswordStatus(),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserTimeZone: (state, { payload }) => {
      if (state.user) {
        state.user.timeZone = payload;
      }
    },
    setCredentials: (state, action) => {
      // state.user = {
      //   id: action.payload.id,
      //   firstName: action.payload.firstName,
      //   lastName: action.payload.lastName,
      //   userType: action.payload.userType,
      //   roleId: action.payload.userRoleId
      // };
      state.user.userType = action.payload?.userType?.toUpperCase();
      state.user.timeZone = action.payload?.timeZone;
      if (action.payload?.status != null) {
        state.user.status = action.payload.status;
      }
      state.isAuthenticated = true;

      // Email verification durumunu kontrol et
      if (action.payload?.isEmailVerified === false) {
        state.showEmailVerificationDialog = true;
      }

      // Password status kontrolü - CreateFirstLogin (2 veya "CreateFirstLogin") ise şifre değiştirme dialog'u göster
      state.passwordStatus = action.payload?.passwordStatus;
      const passwordStatus = action.payload?.passwordStatus;

      // passwordStatus'u localStorage'a kaydet
      if (passwordStatus !== null && passwordStatus !== undefined) {
        localStorage.setItem(PASSWORD_STATUS_KEY, JSON.stringify(passwordStatus));
      }

      // Hem sayısal değer (2) hem de string değer ("CreateFirstLogin") kontrolü
      if (passwordStatus === 2 || passwordStatus === "CreateFirstLogin" || passwordStatus === "CreateFirstLogin".toLowerCase()) {
        state.showPasswordChangeDialog = true;
        state.showFirstLoginPasswordResetDialog = true;
      }

      localStorage.setItem(AUTH_TOKEN_KEY, action.payload.token);

    },
    setDoctorProfile: (state, { payload }) => {
      const rawStatus = payload.status ?? payload.Status;
      const normalizedStatus =
        rawStatus === 2 || rawStatus === "2" || rawStatus === "Pending"
          ? "PendingReview"
          : rawStatus;
      state.isAuthenticated = true;
      state.user = {
        id: payload.id,
        firstName: payload.firstName,
        lastName: payload.lastName,
        userType: payload.userType,
        phoneNumber: payload.phoneNumber,
        email: payload.email,
        branch: payload.primarySpecialty ?? payload.branch,
        primarySpecialty: payload.primarySpecialty ?? payload.branch,
        degree: payload.degree,
        gender: payload.gender,
        rank: payload.rank,
        messageRooms: payload.userMessageRooms,
        education: payload.education,
        background: payload.background,
        profilePhotoName: payload?.profilePhotoName ? payload?.profilePhotoName : "",
        isRegistrationCompleted: payload.isRegistrationCompleted,
        timeZone: payload.timeZone,
        status: normalizedStatus,
        profession: payload.profession ?? payload.specialtyCategory,
        specialtyCategory: payload.profession ?? payload.specialtyCategory, // legacy alias
      };
    },
    setPatientInformation: (state, { payload }) => {
      state.user.firstName = payload.firstName;
      state.user.lastName = payload.lastName;
      state.user.identityNumber = payload.identityNumber;
      state.user.gender = payload.gender;
      state.user.birthDate = payload.birthDate;
      state.user.timeZone = payload.timeZone;
      if (payload.identityNumber !== undefined && payload.identityNumber !== "" && payload.identityNumber !== null) {
        state.user.hasUpdatedProfile = true;
      } else state.user.hasUpdatedProfile = false;
    },
    setPatientHealthBasic: (state, { payload }) => {
      state.user.healthBasic = payload;
      if (!state.user.additionalInformations) state.user.additionalInformations = {};
      state.user.additionalInformations.healthBasic = payload;
    },
    setPatientAddress: (state, { payload }) => {
      state.user.billingInformations.registrationAddress = payload.registrationAddress;
      state.user.billingInformations.city = payload.city;
      state.user.billingInformations.country = payload.country;
      state.user.billingInformations.zipCode = payload.zipCode;
      state.user.billingInformations.billingEmail = payload.billingEmail;
    },
    setPatientAdditionalInformations: (state, { payload }) => {
      state.user.healthBasic = payload;
      state.user.additionalInformations = { healthBasic: payload };
    },
    setPatientProfile: (state, { payload }) => {
      state.isAuthenticated = true;
      state.user = {
        id: payload.id,
        firstName: payload.firstName,
        lastName: payload.lastName,
        userType: payload.userType,
        email: payload.email,
        gender: payload.gender,
        messageRooms: payload.userMessageRooms,
        treatmentDemands: payload.treatmentDemands,
        identityNumber: payload.identityNumber,
        countryCode: payload.countryCode,
        phoneNumber: payload.phoneNumber,
        hasUpdatedProfile: payload.identityNumber !== undefined && payload.identityNumber !== "" && payload.identityNumber !== null ? true : false,
        birthDate: payload.birthDate,
        timeZone: payload.timeZone,
        // identityNumber: payload.identityNumber.slice(9),
        billingInformations: {
          billingEmail: payload.billingEmail,
          city: payload.city,
          country: payload.country,
          zipCode: payload.zipCode,
          registrationAddress: payload.registrationAddress
        },
        healthBasic: payload.healthBasic || null,
        additionalInformations: {
          healthBasic: payload.healthBasic || null,
        }
      }
    },

    setEmployeeProfile: (state, { payload }) => {
      state.isAuthenticated = true;
      state.user = {
        id: payload.id,
        firstName: payload.firstName,
        lastName: payload.lastName,
        userType: payload.userType,
        email: payload.email,
        gender: payload.gender,
        phoneNumber: payload.phoneNumber,
        timeZone: payload.timeZone,
      };
    },
    setAssistantProfile: (state, { payload }) => {
      state.isAuthenticated = true;
      // Backend'den gelen tüm verileri kaydet (doctor dahil)
      state.user = {
        id: payload.id,
        firstName: payload.firstName,
        lastName: payload.lastName,
        userType: payload.userType,
        email: payload.email,
        gender: payload.gender,
        phoneNumber: payload.phoneNumber,
        countryCode: payload.countryCode,
        timeZone: payload.timeZone,
        doctorId: payload.doctorId,
        doctor: payload.doctor, // Doktor bilgilerini de kaydet
        isActive: payload.isActive,
        isEmailVerified: payload.isEmailVerified,
        passwordStatus: payload.passwordStatus,
        createdAt: payload.createdAt,
        updatedAt: payload.updatedAt,
      };

      // passwordStatus kontrolü - CreateFirstLogin ise dialog'u göster
      if (payload.passwordStatus) {
        state.passwordStatus = payload.passwordStatus;
        // passwordStatus'u localStorage'a kaydet
        localStorage.setItem(PASSWORD_STATUS_KEY, JSON.stringify(payload.passwordStatus));

        // CreateFirstLogin kontrolü
        const passwordStatus = payload.passwordStatus;
        if (passwordStatus === 2 || passwordStatus === "CreateFirstLogin" || passwordStatus === "CreateFirstLogin".toLowerCase()) {
          state.showPasswordChangeDialog = true;
          state.showFirstLoginPasswordResetDialog = true;
        }
      }
    },
    updateEmployeeProfile: (state, { payload }) => {
      state.user = { ...state.user, ...payload };
    },
    updateProfilePhoto: (state, { payload }) => {
      state.user.profilePhotoName = payload;
    },
    hideEmailVerificationDialog: (state) => {
      state.showEmailVerificationDialog = false;
    },
    openEmailVerificationDialog: (state) => {
      state.showEmailVerificationDialog = true;
    },
    hidePasswordChangeDialog: (state) => {
      state.showPasswordChangeDialog = false;
    },
    updatePasswordStatus: (state, action) => {
      state.passwordStatus = action.payload;
      // passwordStatus'u localStorage'dan sil veya güncelle
      if (action.payload === null || action.payload === undefined) {
        localStorage.removeItem(PASSWORD_STATUS_KEY);
      } else {
        localStorage.setItem(PASSWORD_STATUS_KEY, JSON.stringify(action.payload));
      }
    },
    hideFirstLoginPasswordResetDialog: (state) => {
      state.showFirstLoginPasswordResetDialog = false;
    },
    showFirstLoginPasswordResetDialog: (state) => {
      state.showFirstLoginPasswordResetDialog = true;
    },

    clearCredentials: (state) => {
      state.user = {};
      state.isAuthenticated = false;
      state.role = {};
      state.showEmailVerificationDialog = false;
      state.showPasswordChangeDialog = false;
      state.showFirstLoginPasswordResetDialog = false;
      state.passwordStatus = null;
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(MENU_ITEMS_KEY);
      localStorage.removeItem(PASSWORD_STATUS_KEY);
    },
    setUserRole: (state, { payload }) => {
      // Safety check: Don't set role if payload is undefined or null
      if (!payload) {
        console.warn('[authSlice] setUserRole called with undefined/null payload, skipping');
        return;
      }
      var roleList = {};
      payload?.permissions?.map((element) => {
        roleList = { ...roleList, [element.ServiceName]: element.Operations };
        return null;
      });
      state.role = { ...payload, permissions: roleList };
    },
    decodeToken: (state) => {
      //TODO: this action must be replaced by backend action, refreshToken
      console.log('🔍 [authSlice] Token decode işlemi başlatılıyor...');
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (token === null || token.length === 0) {
        console.log('❌ [authSlice] Token bulunamadı, state temizleniyor...');
        state.user = {};
        state.isAuthenticated = false;
        localStorage.removeItem(AUTH_TOKEN_KEY);
      } else {
        try {
          const decodedToken = jwtDecode(token);
          console.log('🔓 [authSlice] Decoded token:', decodedToken);
          if (Date.now() < decodedToken.exp * 1000) {
            console.log('✅ [authSlice] Token geçerli, kullanıcı bilgileri ayarlanıyor...');
            // state.isAuthenticated = false;
            // localStorage.removeItem(AUTH_TOKEN_KEY);
          } else {
            console.log('⏰ [authSlice] Token süresi dolmuş');
            state.user = {
              id: decodedToken.id,
              firstName: decodedToken.firstName,
              lastName: decodedToken.lastName,
              userType: decodedToken.userType,
              timeZone: decodedToken.timeZone,
            };
            state.isAuthenticated = true;
          }
        } catch (error) {
          console.log('💥 [authSlice] Token decode hatası:', error);
          state.user = {};
          state.isAuthenticated = false;
          localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      }
    },
  },
});

export const {
  setCredentials,
  setUserTimeZone,
  clearCredentials,
  setUserRole,
  setDoctorProfile,
  setEmployeeProfile,
  setAssistantProfile,
  setPatientProfile,
  decodeToken,
  updateEmployeeProfile,
  setPatientAdditionalInformations,
  setPatientInformation,
  setPatientHealthBasic,
  setPatientAddress,
  updateProfilePhoto,
  hideEmailVerificationDialog,
  openEmailVerificationDialog,
  hidePasswordChangeDialog,
  updatePasswordStatus,
  hideFirstLoginPasswordResetDialog,
  showFirstLoginPasswordResetDialog
} = authSlice.actions;
export default authSlice.reducer;
